import { createReducer, on, select } from '@ngrx/store';
import * as FandCActions from './fandc-generator.actions';
import {
  initialFandCGeneratorState,
} from '../types/fandc-generator.state';
import { Frequency, FrequencyOffsetOption } from '../services/frequencyoptions.service';

// Helper function to get the due label from the offset value
const getDueLabel = (frequency: Frequency, offsetValue: string | Date | null, frequencyOffsetOptions: FrequencyOffsetOption[]): string => {
  if (!frequency || !offsetValue || !frequencyOffsetOptions) {
    return offsetValue?.toString() || '';
  }
  
  // Handle One-Time frequency case where offsetValue is a Date
  if (frequency === 'One-Time' && offsetValue instanceof Date) {
    return offsetValue.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  }
  
  // For other frequencies, find the matching option and return its label
  const offsetValueString = offsetValue?.toString() || '';
  const matchingOption = frequencyOffsetOptions.find(option => option.value === offsetValueString);
  return matchingOption ? matchingOption.label : offsetValueString;
};

export const fandCGeneratorReducer = createReducer(
  initialFandCGeneratorState,
  on(FandCActions.fetchTemplatesSuccess, (state, { data }) => {
    // console.log('fetchTemplatesSuccess:', data);
    // If data exists and has at least one template, set selectedTemplateType
    const selectedTemplateType = data && data.length > 0 ? data[0].templateType : state.selectedTemplateType;
    return {
      ...state,
      selectedTemplateType,
      templates: data, 
    };
  }),
  on(FandCActions.fetchTemplateTypesSuccess, (state, { data }) => {
    // console.log('fetchTemplateTypesSuccess:', data);
    return {
      ...state,
      templateTypes: data, 
    };
  }),
  on(FandCActions.fetchRootTemplateSuccess, (state, { data }) => {
    // console.log('fetchRootTemplateSuccess:', data);
    return {
      ...state,
      rootTemplate: data, 
    };
  }),  
  on(FandCActions.fetchDefaultTasksForTemplateSuccess, (state, { data }) => {
      // console.log('fetchDefaultTasksForTemplateSuccess:', data);
      // console.log('state:', state);
      
      // Find the label for the selectedReportEntity value
      const reportEntityLabel =
        state.reportEntityOptions && state.selectedReportEntity
        ? (state.reportEntityOptions.find(
          (option: any) => option.value === state.selectedReportEntity
          )?.label ?? state.selectedReportEntity)
        : state.selectedReportEntity ?? '';



    const prototypeTasks = data.tasks
      ? data.tasks.map((task: any) => ({
          taskId: task.taskId ?? '',
          type: state.selectedTemplateType ?? '',
          category: data.templateName ?? '',
          taskName: task.taskName ?? '',
          description: task.taskDescription ?? '',
          reportEntity: state.selectedReportEntity ?? '',
          reportEntityLabel: reportEntityLabel,
          entityName: state.selectedEntityName ?? '',
          referenceDate: state.selectedFrequency === 'One-Time' && state.selectedFrequencyOffsetValue && typeof state.selectedFrequencyOffsetValue === 'object' && 'getTime' in (state.selectedFrequencyOffsetValue as any)
            ? state.selectedFrequencyOffsetValue as Date
            : state.selectedReferenceDate,
          firstDueDate: state.firstDueDate,
          untilDate: state.selectedFrequency === 'One-Time' ? null : state.selectedMaturityDate,
          frequency: state.selectedFrequency,
          due: getDueLabel(state.selectedFrequency as Frequency, state.selectedFrequencyOffsetValue, state.frequencyOffsetOptions),
          format: state.selectedFormatOption ?? '',
          reference: task.reference ?? '',
          notes: task.notes ?? '',
          assignedUserName: task.assignedUserName ?? '',
          team: task.team ?? '',
          templateId: data.templateId ?? '',
        }))
      : [];
    return {
      ...state,
      category: data.templateName ?? state.category,
      defaultTasksForTemplate: data.tasks,
      prototypeTasks: prototypeTasks,
    };
  }),
  on(FandCActions.updatePrototypeTasks, (state, { changeData }) => {
    // changeEvent should have: key (taskId), changes (partial task)
    // console.log('changeData:', changeData);
    // console.log('state.prototypeTasks:', state.prototypeTasks);
    // console.log('state.prototypeTasks:', state.selectedPrototypeTasks);
    const updatedPrototypeTasks = state.selectedPrototypeTasks
      ? state.selectedPrototypeTasks.map(task =>
          task.taskId === changeData.taskId
            ? { ...task, ...changeData }
            : task
        )
      : [];
      // console.log('updatedPrototypeTasks:', updatedPrototypeTasks);
    return {
      ...state,
      selectedPrototypeTasks: updatedPrototypeTasks,
    };
  }),
  on(FandCActions.addToSelectedPrototypeTasks, (state) => {
    // Add new tasks to the existing prototypeTasks array
    if (!state.selectedDefaultTasks || state.selectedDefaultTasks.length === 0) {
      // Return state with selectedPrototypeTasks unchanged (or empty array if undefined)
      return {
        ...state,
        selectedPrototypeTasks: state.selectedPrototypeTasks || []
      };
    }
    // Filter prototypeTasks to only those whose taskId is in selectedDefaultTasks
    const tasksToAdd = (state.prototypeTasks || []).filter(task =>
      state.selectedDefaultTasks.includes(task.taskId)
    );
    // Add filtered tasks to selectedPrototypeTasks, avoiding duplicates by taskId
    const existingTaskIds = new Set((state.selectedPrototypeTasks || []).map(task => task.taskId));
    const updatedSelectedPrototypeTasks = [
      ...(state.selectedPrototypeTasks || []),
      ...tasksToAdd.filter(task => !existingTaskIds.has(task.taskId))
    ];
    // console.log('updatedSelectedPrototypeTasks:', updatedSelectedPrototypeTasks);
    return {
      ...state,
      selectedPrototypeTasks: updatedSelectedPrototypeTasks,
      selectedDefaultTasks: []
    };
  }),  
  on(FandCActions.updateSelectedDefaultTasks, (state, { data }) => {
    return {
      ...state,
      selectedDefaultTasks: data,
    };
  }),
  on(FandCActions.setLoanNumber, (state, { selectedLoan }) => {
    // console.log('setLoanNumber:', selectedLoan?.name);
    return { ...state, selectedLoanNumber: selectedLoan?.name };
  }),
  on(FandCActions.setSelectedTemplateType, (state, { templateType }) => {
    // console.log('setSelectedTemplateType:', templateType);
    return { ...state, selectedTemplateType: templateType };
  }),
  on(FandCActions.setCategory, (state, { category }) => {
    // console.log('setCategory:', category);
    return { ...state, category };
  }),
  on(FandCActions.setTasks, (state, { tasks }) => {
    // console.log('setTasks:', tasks);
    return { ...state, tasks };
  }),
  on(FandCActions.setMaturityDate, (state, { selectedMaturityDate }) => {
    // console.log('setMaturityDate:', selectedMaturityDate);
    const updatedPrototypeTasks = state.prototypeTasks
      ? state.prototypeTasks.map(task => ({
        ...task,
        untilDate: selectedMaturityDate
      }))
      : [];    
    return { ...state, selectedMaturityDate, prototypeTasks: updatedPrototypeTasks };
  }),  
  on(FandCActions.setReferenceDate, (state, { selectedReferenceDate }) => {
    // console.log('setReferenceDate');
    const updatedPrototypeTasks = state.prototypeTasks
      ? state.prototypeTasks.map(task => ({
        ...task,
        referenceDate: selectedReferenceDate
      }))
      : [];
    return { ...state, selectedReferenceDate, prototypeTasks: updatedPrototypeTasks };
  }),
  on(FandCActions.setFrequency, (state, { selectedFrequency }) => {
    // When frequency changes, the due label mapping may also change
    // So we need to update the due label based on the new frequency
    const dueLabel = getDueLabel(selectedFrequency as Frequency, state.selectedFrequencyOffsetValue, state.frequencyOffsetOptions);
    
    const updatedPrototypeTasks = state.prototypeTasks
        ? state.prototypeTasks.map(task => {
            return { 
              ...task, 
              frequency: selectedFrequency,
              due: dueLabel,
              untilDate: selectedFrequency === 'One-Time' ? null : state.selectedMaturityDate,
              referenceDate: selectedFrequency === 'One-Time' && state.selectedFrequencyOffsetValue && typeof state.selectedFrequencyOffsetValue === 'object' && 'getTime' in (state.selectedFrequencyOffsetValue as any)
                ? state.selectedFrequencyOffsetValue as Date
                : state.selectedReferenceDate
            };
        })
        : [];
    return { ...state, selectedFrequency, prototypeTasks: updatedPrototypeTasks };
  }),
  on(FandCActions.setFrequencyOffsetValue, (state, { selectedFrequencyOffsetValue }) => {
    const dueLabel = getDueLabel(state.selectedFrequency as Frequency, selectedFrequencyOffsetValue, state.frequencyOffsetOptions);
    const updatedPrototypeTasks = state.prototypeTasks
      ? state.prototypeTasks.map(task => ({
        ...task,
        due: dueLabel,
        untilDate: state.selectedFrequency === 'One-Time' ? null : state.selectedMaturityDate,
        referenceDate: state.selectedFrequency === 'One-Time' && selectedFrequencyOffsetValue && typeof selectedFrequencyOffsetValue === 'object' && 'getTime' in (selectedFrequencyOffsetValue as any)
          ? selectedFrequencyOffsetValue as Date
          : state.selectedReferenceDate
      }))
      : [];
    return { ...state, selectedFrequencyOffsetValue, prototypeTasks: updatedPrototypeTasks };
  }),
  on(FandCActions.setFrequencyOptions, (state, { options }) => {
    return {
      ...state,
      frequencyOptions: options
    };
  }),
  on(FandCActions.setFrequencyOffsetOptions, (state, { options }) => {
    return {
      ...state,
      frequencyOffsetOptions: options
    };
  }),
  on(FandCActions.setFormatOptions, (state, { options }) => {
    return {
      ...state,
      formatOptions: options
    };
  }),
  on(FandCActions.setFormatOption, (state, { selectedFormatOption }) => {
    const updatedPrototypeTasks = state.prototypeTasks
      ? state.prototypeTasks.map(task => ({
        ...task,
        format: selectedFormatOption
      }))
      : [];    
    return { ...state, selectedFormatOption, prototypeTasks: updatedPrototypeTasks };    
  }),  

  on(FandCActions.setFirstDueDate, (state, { firstDueDate }) => ({
    ...state,
    firstDueDate,
    prototypeTasks: state.prototypeTasks
      ? state.prototypeTasks.map(task => ({ ...task, firstDueDate }))
      : []
  })),
  on(FandCActions.fetchLoansSuccess, (state, { data }) => {
    // console.log('fetchLoansSuccess:', data);
    return {
      ...state,
      loans: data,
    };
  }),
  on(FandCActions.fetchLoanDetailSuccess, (state, { data }) => {
    // console.log('fetchLoanDetailSuccess:', data);
    return {
      ...state,
      selectedMaturityDate: data?.facilityDetail?.maturityDate,
      selectedReferenceDate: data?.facilityDetail?.originationDate,
      selectedLoanDetail: data,
    };
  }),
  on(FandCActions.setLoanDetail, (state, { loanDetail }) => {
    // console.log('setLoanDetail:', loanDetail);
    return {
      ...state,
      selectedMaturityDate: loanDetail?.facilityDetail?.maturityDate,
      selectedReferenceDate: loanDetail?.facilityDetail?.originationDate,      
      selectedLoanDetail: loanDetail,
    };
  }),  
  on(FandCActions.fetchReportEntityOptionsSuccess, (state, { data }) => {
    // console.log('fetchReportEntityOptionsSuccess:', data);
    return {
      ...state,
      reportEntityOptions: data,
    };
  }),
  on(FandCActions.setReportEntity, (state, { selectedReportEntity }) => {
    // console.log('setReportEntity:', selectedReportEntity);
    return {
      ...state,
      selectedReportEntity: selectedReportEntity,
    };
  }),  
  on(FandCActions.setEntityNameOptions, (state, { options }) => {
    // console.log('setEntityNameOptions:', options);
    return {
      ...state,
      entityNameOptions: options,
    };
  }),
  on(FandCActions.setEntityName, (state, { selectedEntityName }) => {
      // Find the label for the selectedReportEntity value
      const reportEntityLabel =
        state.reportEntityOptions && state.selectedReportEntity
        ? (state.reportEntityOptions.find(
          (option: any) => option.value === state.selectedReportEntity
          )?.label ?? state.selectedReportEntity)
        : state.selectedReportEntity ?? '';
      // console.log('setEntityName:', selectedEntityName);
      // console.log('reportEntityLabel:', reportEntityLabel);
      // Update prototype tasks with the latest entity name and report entity info
      const updatedPrototypeTasks = state.prototypeTasks
        ? state.prototypeTasks.map(task => ({
            ...task,
            entityName: selectedEntityName,
            reportEntity: state.selectedReportEntity ?? '',
            reportEntityLabel: reportEntityLabel
          }))
        : [];

        return {
      ...state,
      prototypeTasks: updatedPrototypeTasks,
      selectedEntityName: selectedEntityName,
    };
  }),    
  on(FandCActions.fetchGeneratedTasksSuccess, (state, { tasks }) => {
    // console.log('fetchGeneratedTasksSuccess:', tasks);
    return {
      ...state,
      generatedTasks: tasks,
    };
  }),
  on(FandCActions.updateGeneratedTasks, (state, { changeData }) => {
    // changeEvent should have: key (taskId), changes (partial task)
    // console.log('updateGeneratedTasks:', changeData);
    const updatedGeneratedTasks = state.prototypeTasks
      ? state.generatedTasks.map(task =>
          task.taskId === changeData.taskId
            ? { ...task, ...changeData }
            : task
        )
      : [];
      // console.log('updatedGeneratedTasks:', updatedGeneratedTasks);
    return {
      ...state,
      generatedTasks: updatedGeneratedTasks,
    };
  }),
  on(FandCActions.setSelectedPrototypeTasks, (state, { selectedPrototypeTasks }) => {
    return {
      ...state,
      selectedPrototypeTasks: selectedPrototypeTasks,
    };
  }),
  on(FandCActions.removePrototypeTask, (state, { taskId }) => {
    const filteredTasks = state.selectedPrototypeTasks.filter(task => task.taskId !== taskId);
    // console.log('Removing task with ID:', taskId);
    // console.log('Tasks after removal:', filteredTasks);
    return {
      ...state,
      selectedPrototypeTasks: filteredTasks,
    };
  }),
  on(FandCActions.setTaskId, (state, { taskId }) => {
    return {
      ...state,
      taskId: taskId,
    };
  }),
  on(FandCActions.setTaskGenerationMetadataId, (state, { taskGenerationMetadataId }) => {
    return {
      ...state,
      taskGenerationMetadataId: taskGenerationMetadataId,
    };
  }),          
  on(FandCActions.resetFandCGenerator, () => {
    // console.log('resetFandCGenerator');
    return initialFandCGeneratorState;
  })
);
